/* eslint-disable no-alert */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { FC, useContext, useEffect, useState } from 'react';
import Calendar, { Detail } from 'react-calendar';
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
} from '@mui/icons-material';
import moment from 'moment';

import { UseFormik } from '@/utils/Formik/types';
import { ErrorMessage, useToastErrorMessage } from '@/utils/Messages';

import { getStatementAvailability } from '@/services/PropertyStatementAvailability/request';

import { useLoader } from '@/context/LoaderContext';
import { useToast } from '@/context/ToastContext';

import { useOwnerExtract } from '@/hooks/OwnerPage/OwnerExtract/useOwnerExtract';
import { useSearch } from '@/hooks/SearchHook/useSearch';

import { CalendarContext } from '@/components/Calendar/Calendar';
import { FastDataForwardContext } from '@/components/Calendar/FastDataForwardButton/FastDataForwardButton';

import { FastDataFowardModal } from './styles';

interface FastDataForwardModalProps {
  id: string;
  setDates: Function;
  onClose: () => void;
  setSelectedDay?: Function;
  setSelectedMonth?: Function;
  setSelectedYear?: Function;
  showTodayButton?: boolean;
  defaultView?: Detail | undefined;
  defaultValue?: Date | undefined;
  isOwnerPage?: boolean;
  isFinancialStatementCalendar?: boolean;
  minDate?: Date;
  formik?: UseFormik<any>;
}

const FastDataForwardModal: FC<FastDataForwardModalProps> = ({
  id,
  onClose,
  setDates,
  setSelectedDay,
  showTodayButton,
  defaultView,
  setSelectedMonth,
  setSelectedYear,
  defaultValue,
  isOwnerPage = false,
  isFinancialStatementCalendar = false,
  minDate,
}) => {
  const { property } = useOwnerExtract();
  const { dateSelected, setDateSelected } = useContext(FastDataForwardContext);
  const [calendarValue, setCalendarValue] = useState(dateSelected);
  const calendarStartValue = new Date();
  const [isToday, setIsToday] = useState(true);
  const [responseFarthestMonth, setResponseFarthestMonth] = useState('');

  const { getReservation } = useContext(CalendarContext);
  const { filteredResults } = useSearch();
  const { setLoad } = useLoader();

  const toastErrorRequest = useToastErrorMessage();
  const toast = useToast();

  const handleOutClick = (event: any) => {
    if (event.target.id === id) onClose();
  };

  const handleOnClickMonth = (month: Date) => {
    if (setSelectedMonth) {
      setSelectedMonth(month);
    }
  };

  const handleOnClickYear = (year: Date) => {
    if (setSelectedYear) {
      setSelectedYear(year);
    }
  };

  const handleOnClickDay = async (value: Date) => {
    if (setSelectedDay) {
      setSelectedDay(value);
    }

    try {
      setLoad(true);
      setCalendarValue(value);
      setDateSelected(value);
      const prevDate = Array.from({ length: 15 }, (_, days: number) =>
        moment(
          moment(value)
            .add(-(days + 1), 'days')
            .toDate(),
        ),
      ).reverse();
      const nextDate = Array.from({ length: 15 }, (_, days: number) =>
        moment(
          moment(value)
            .add(days + 1, 'days')
            .toDate(),
        ),
      );
      setDates([...prevDate, moment(value), ...nextDate]);
      await getReservation(filteredResults, [...prevDate, moment(value), ...nextDate]);
      const element: HTMLElement | null = document.getElementById(
        moment(value).format('YYYY-MM-DD'),
      );
      element?.scrollIntoView({ inline: 'center' });
      setLoad(false);
    } catch (e: unknown) {
      if (e instanceof Error) {
        setLoad(false);
        toast.error(e.message || ErrorMessage.default());
        toastErrorRequest(e);
      }
    }
  };

  const handleOnClickToday = async () => {
    try {
      setLoad(true);
      setDateSelected(calendarStartValue);
      setIsToday(true);
      const prevDate = Array.from({ length: 15 }, (_, days: number) =>
        moment(
          moment(calendarStartValue)
            .add(-(days + 1), 'days')
            .toDate(),
        ),
      ).reverse();
      const nextDate = Array.from({ length: 15 }, (_, days: number) =>
        moment(
          moment(calendarStartValue)
            .add(days + 1, 'days')
            .toDate(),
        ),
      );
      setDates([...prevDate, moment(calendarStartValue), ...nextDate]);
      await getReservation(filteredResults, [...prevDate, moment(calendarStartValue), ...nextDate]);
      const element: HTMLElement | null = document.getElementById(
        moment(calendarStartValue).format('YYYY-MM-DD'),
      );
      element?.scrollIntoView({ inline: 'center' });
      setLoad(false);
    } catch (e: unknown) {
      if (e instanceof Error) {
        setLoad(false);
        toast.error(e.message || ErrorMessage.default());
        toastErrorRequest(e);
      }
    }
  };

  useEffect(() => {
    if (
      dateSelected.getDate() === calendarStartValue.getDate() &&
      dateSelected.getMonth() === calendarStartValue.getMonth() &&
      dateSelected.getFullYear() === calendarStartValue.getFullYear()
    ) {
      setIsToday(true);
    } else {
      setIsToday(false);
    }
  }, [dateSelected]);

  useEffect(() => {
    const fetchFarthestMonth = async () => {
      if (property.id !== undefined && property.id !== -1) {
        try {
          const response = await getStatementAvailability(property.id);
          setResponseFarthestMonth(response.farthest_month);
        } catch (e) {
          if (e instanceof Error && e.message === 'reservation or approved expense not found') {
            toast.error(
              'Não existem reservas concluídas ou despesas aprovadas para gerar um extrato',
            );
          }
        }
      }
    };
    fetchFarthestMonth();
  }, [property.id]);

  const extractButtonIsActive = (date: Date) => {
    if (!responseFarthestMonth) return false;

    const year = date.getFullYear();
    const monthId = date.getMonth();

    if (year < 2024) return false;

    const [farthestMonth, farthestYear] = responseFarthestMonth.split('-').map(Number);

    if (year > farthestYear || (year === farthestYear && monthId > farthestMonth - 1)) {
      return false;
    }

    return true;
  };

  return (
    <FastDataFowardModal
      id={id}
      onClick={handleOutClick}
      isToday={isToday}
      isOwner={isOwnerPage}
      isFinancialStatementCalendar={isFinancialStatementCalendar}
    >
      <div className="container">
        <div className="content">
          {isFinancialStatementCalendar ? (
            <Calendar
              defaultView={defaultView}
              className="react-calendar"
              onChange={setCalendarValue}
              value={defaultValue || calendarValue}
              prevLabel={<ArrowBackIosIcon />}
              nextLabel={<ArrowForwardIosIcon />}
              next2Label={null}
              prev2Label={null}
              defaultValue={defaultValue}
              tileClassName={({ date }) =>
                extractButtonIsActive(date) ? 'react-calendar__tile--active' : 'tile-class'
              }
              onClickDay={(d) => (extractButtonIsActive(d) ? handleOnClickDay(d) : {})}
              onClickMonth={(d) => (extractButtonIsActive(d) ? handleOnClickMonth(d) : {})}
              onClickYear={(d) => (extractButtonIsActive(d) ? handleOnClickYear(d) : {})}
              formatShortWeekday={(_locale, value) =>
                ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'][value.getDay()]
              }
              tileDisabled={({ date }) => !extractButtonIsActive(date)}
              view="year"
              minDate={minDate}
            />
          ) : (
            <Calendar
              defaultView={defaultView}
              className="react-calendar"
              onChange={setCalendarValue}
              value={defaultValue || calendarValue}
              prevLabel={<ArrowBackIosIcon />}
              nextLabel={<ArrowForwardIosIcon />}
              next2Label={null}
              prev2Label={null}
              defaultValue={defaultValue}
              tileClassName="tile-class"
              onClickDay={(day) => handleOnClickDay(day)}
              onClickMonth={(month) => handleOnClickMonth(month)}
              onClickYear={(year) => handleOnClickYear(year)}
              formatShortWeekday={(_locale, value) =>
                ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'][value.getDay()]
              }
            />
          )}
        </div>
        {showTodayButton && (
          <div className="today">
            <button type="button" onClick={() => handleOnClickToday()}>
              Hoje
            </button>
          </div>
        )}
      </div>
    </FastDataFowardModal>
  );
};

FastDataForwardModal.defaultProps = {
  showTodayButton: true,
  defaultView: 'month',
  defaultValue: undefined,
};

export default FastDataForwardModal;
